import React, { useEffect, useState, useCallback } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import Header from "../../components/Header";
import Footer from "../../components/Footer";
import { api } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { toast } from "sonner";
import { ShieldCheck, CurrencyDollar, ArrowRight, Warning } from "@phosphor-icons/react";

export default function VirtualPayouts() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState(false);
  const [paymentsEnabled, setPaymentsEnabled] = useState(false);

  const loadStatus = useCallback(async () => {
    try {
      const [{ data: s }, { data: ps }] = await Promise.all([
        api.get("/virtual/payouts/status"),
        api.get("/virtual/payments-status"),
      ]);
      setStatus(s);
      setPaymentsEnabled(ps.enabled);
    } catch (err) {
      console.error("Payouts status error:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!user) { navigate("/login?redirect=/virtual/freelancer/payouts"); return; }
    loadStatus();
  }, [user, navigate, loadStatus]);

  useEffect(() => {
    if (searchParams.get("completed") === "1") {
      toast.success("Onboarding submitted. Verifying with Stripe…");
      loadStatus();
    }
  }, [searchParams, loadStatus]);

  const startOnboarding = async () => {
    setStarting(true);
    try {
      const { data } = await api.post("/virtual/payouts/onboard");
      window.location.href = data.url;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not start onboarding");
      setStarting(false);
    }
  };

  if (loading) return <div className="min-h-screen grid place-items-center text-slate-500">Loading…</div>;
  const isActive = status?.charges_enabled && status?.payouts_enabled;

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <section className="pt-24 pb-16 max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <Link to="/virtual" className="text-xs uppercase tracking-[0.2em] font-semibold text-slate-500 hover:text-slate-900">← Hire Virtual Staff</Link>
        <h1 className="font-display text-4xl mt-2 mb-2">Set up payouts</h1>
        <p className="text-sm text-slate-600 mb-8">Connect your bank account via Stripe so clients can pay you for completed virtual projects. Takes about 3 minutes. One-time setup.</p>

        {!paymentsEnabled && (
          <div className="border-l-4 border-amber-400 bg-amber-50 p-5 mb-6" data-testid="payments-pending-notice">
            <div className="flex gap-3 mb-2">
              <Warning size={20} weight="bold" className="text-amber-600 flex-shrink-0 mt-0.5" />
              <div className="font-semibold text-amber-900">Stripe payouts setup is being finalized</div>
            </div>
            <div className="text-sm text-amber-900 space-y-2">
              <p>We&apos;re finishing setup with Stripe to enable in-platform escrow + automatic payouts (with a 10% platform fee).</p>
              <p>
                <b>In the meantime:</b> when you&apos;re hired for a project, please coordinate payment directly with the client (e-transfer, PayPal, Wise, etc.). You can still apply, get hired, chat, mark projects complete, and leave reviews — everything works except automated money movement.
              </p>
              <p className="text-xs text-amber-800">You can pre-fill your payout details below so you&apos;re ready the moment we flip the switch.</p>
            </div>
          </div>
        )}

        {isActive ? (
          <div className="border-l-4 border-green-500 bg-green-50 p-5" data-testid="payouts-active">
            <div className="flex items-center gap-2 mb-1">
              <ShieldCheck size={20} weight="fill" className="text-green-600" />
              <div className="font-display text-xl">Payouts active</div>
            </div>
            <p className="text-sm text-slate-700 mb-2">Your Stripe Connect account is verified and ready. Clients can fund projects and your earnings will be paid out automatically after each release.</p>
            <div className="text-xs text-slate-500 font-mono">Account: {status.account_id}</div>
          </div>
        ) : status?.details_submitted ? (
          <div className="border-l-4 border-blue-500 bg-blue-50 p-5" data-testid="payouts-pending">
            <div className="font-display text-xl mb-1">Verification in progress</div>
            <p className="text-sm text-slate-700">Stripe is reviewing your details. This usually takes a few minutes but can take up to 24 hours.</p>
            <button onClick={loadStatus} className="mt-3 text-xs uppercase tracking-wider font-semibold text-brand">Refresh status →</button>
          </div>
        ) : (
          <div className="border border-slate-200 p-6">
            <div className="font-display text-xl mb-2">Get paid for your work</div>
            <ul className="text-sm text-slate-700 space-y-2 mb-5">
              <li className="flex items-start gap-2"><CurrencyDollar size={16} className="text-brand flex-shrink-0 mt-0.5" /> Get 90% of every funded project (CanHireSolution keeps 10%)</li>
              <li className="flex items-start gap-2"><ShieldCheck size={16} className="text-brand flex-shrink-0 mt-0.5" /> Funds released the moment the client approves your work</li>
              <li className="flex items-start gap-2"><ArrowRight size={16} className="text-brand flex-shrink-0 mt-0.5" /> Direct deposit to your bank — supports 35+ countries via Stripe</li>
            </ul>
            <button onClick={startOnboarding} disabled={starting} className="bg-brand text-white px-6 py-3 font-semibold hover:bg-[#122148] disabled:opacity-50" data-testid="start-onboarding-btn">
              {starting ? "Redirecting…" : "Start Stripe onboarding →"}
            </button>
            <p className="text-xs text-slate-500 mt-4">You&apos;ll be redirected to Stripe to provide your ID, bank account, and tax info. Your data goes directly to Stripe — CanHireSolution never sees your bank details.</p>
          </div>
        )}
      </section>
      <Footer />
    </div>
  );
}
